// frontend/src/components/settings/ProfileInfoForm.tsx

import { useState, FormEvent } from 'react';
import { settingsService } from '../../services/settings.service';
import { Profile } from '../../types/auth.types';

interface ProfileInfoFormProps {
  profile: Profile;
  userEmail: string | undefined;
  onSaved: () => void;
}

export function ProfileInfoForm({ profile, userEmail, onSaved }: ProfileInfoFormProps) {
  const [fullName, setFullName] = useState(profile.full_name);
  const [age, setAge] = useState(profile.age ? String(profile.age) : '');
  const [schoolName, setSchoolName] = useState(profile.school_name || '');
  const [interests, setInterests] = useState(profile.interests.join(', '));
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);
    try {
      await settingsService.updateProfile(profile.id, {
        full_name: fullName.trim(),
        age: age ? Number(age) : null,
        school_name: schoolName.trim() || null,
        interests: interests
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setMessage({ text: 'Profil güncellendi! ✅', type: 'success' });
      onSaved();
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Güncelleme başarısız.', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  }

  function handleCopyCode() {
    if (profile.student_code) {
      navigator.clipboard.writeText(profile.student_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <>
      <div className="card">
        <h3 className="parent-section-title">Profil Bilgileri</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Ad Soyad</label>
            <input className="input" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>E-posta</label>
            <input className="input" type="email" value={userEmail ?? ''} disabled />
          </div>
          <div className="form-group">
            <label>Yaş (Kayıttan sonra değiştirilemez)</label>
            <input className="input" type="number" min={4} max={18} value={age} disabled />
          </div>
          <div className="form-group">
            <label>Okul Adı (Kayıttan sonra değiştirilemez)</label>
            <input className="input" type="text" placeholder="Örn: Atatürk Ortaokulu" value={schoolName} disabled />
          </div>
          <div className="form-group">
            <label>İlgi Alanları (virgülle ayır)</label>
            <input
              className="input"
              type="text"
              placeholder="Oyun geliştirme, Python, Yapay Zeka"
              value={interests}
              onChange={(e) => setInterests(e.target.value)}
            />
          </div>

          {message && (
            <p className={message.type === 'success' ? 'auth-info' : 'auth-error'} style={{ marginBottom: 12 }}>
              {message.text}
            </p>
          )}

          <button type="submit" className="lesson-btn primary" disabled={isSaving}>
            {isSaving ? '⏳ Kaydediliyor...' : '💾 Kaydet'}
          </button>
        </form>
      </div>

      {/* Öğrenci Kodu Kartı — sadece öğrenci rolü için */}
      {profile.role === 'student' && profile.student_code && (
        <div className="card" style={{ marginTop: 16, borderColor: 'rgba(99,102,241,0.4)', background: 'rgba(99,102,241,0.04)' }}>
          <h3 className="parent-section-title" style={{ marginBottom: 8 }}>🔑 Öğrenci Kodun</h3>
          <p style={{ color: 'var(--gray)', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
            Bu kodu velilerinle paylaş. Veliler bu kodu veli panelinden girerek seni takip edebilir.
            Bu kod değişmez ve sadece sana aittir.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              className="input"
              type="text"
              value={profile.student_code}
              readOnly
              style={{
                fontFamily: 'monospace',
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: '0.25em',
                textAlign: 'center',
                color: 'var(--primary-light)',
                flex: 1,
                cursor: 'default',
                background: 'var(--dark)',
              }}
            />
            <button
              type="button"
              className="lesson-btn primary"
              onClick={handleCopyCode}
              style={{ whiteSpace: 'nowrap', minWidth: 130 }}
            >
              {copied ? '✅ Kopyalandı!' : '📋 Kopyala'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
